// Live GPS tracking — pure helpers (no React, no I/O): distances, the client-side
// throttle, and how each tracker phase / server health is shown. The server has
// the final say on what is stored (modules/tsp/tracking in the backend); the
// browser only avoids sending fixes that obviously add nothing.

const EARTH_RADIUS_M = 6371008.8;
const toRad = (deg) => (deg * Math.PI) / 180;

export function distanceMeters(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Used until the server's own config arrives (same values as the backend's
// tracking/config.js).
export const DEFAULT_TRACKING_CONFIG = Object.freeze({
  flush_interval_seconds: 15,
  heartbeat_seconds: 30,
  stale_after_seconds: 90,
  no_fix_after_seconds: 120,
  min_distance_m: 15,
  max_path_accuracy_m: 150,
  max_batch_points: 100,
});

// Queue this fix for the server? Yes for the first one, after moving
// min_distance_m, after heartbeat_seconds, or when it is much more accurate
// than the last one queued.
export function shouldQueueFix(fix, lastQueued, config = DEFAULT_TRACKING_CONFIG) {
  if (!lastQueued) return true;
  if (fix.t - lastQueued.t >= config.heartbeat_seconds * 1000) return true;
  if (fix.accuracy < lastQueued.accuracy / 2) return true;
  return distanceMeters(lastQueued, fix) >= config.min_distance_m;
}

// A Geolocation API position -> the fix the API accepts.
export function fixFromPosition(position, now = Date.now()) {
  const { latitude, longitude, accuracy, speed, heading } = position.coords;
  const fix = { lat: latitude, lng: longitude, accuracy: Number.isFinite(accuracy) ? accuracy : 0, t: Number.isFinite(position.timestamp) ? Math.round(position.timestamp) : now };
  if (Number.isFinite(speed) && speed >= 0) fix.speed = speed;
  if (Number.isFinite(heading) && heading >= 0 && heading <= 360) fix.heading = heading;
  return fix;
}

// This device's tracker phases (gpsTracker.js), as shown on the GPS button.
// `tone` picks the badge colour; `on` = the button offers "Disable GPS".
export const GPS_PHASE_UI = Object.freeze({
  off: { label: "GPS OFF", tone: "off", on: false },
  starting: { label: "Requesting location…", tone: "pending", on: true },
  on: { label: "GPS ON · Tracking", tone: "live", on: true },
  searching: { label: "Searching for GPS signal", tone: "warn", on: true },
  unavailable: { label: "Location unavailable", tone: "error", on: true },
  offline: { label: "Offline · saving locally", tone: "warn", on: true },
  paused: { label: "Paused on this device", tone: "warn", on: false },
  denied: { label: "Location permission denied", tone: "error", on: false },
  unsupported: { label: "Location not supported", tone: "error", on: false },
  ended: { label: "Tracking ended", tone: "off", on: false },
  error: { label: "Tracking error", tone: "error", on: false },
});

// The server's view of a tracker (tracking/service.js#sessionHealth).
export const HEALTH_UI = Object.freeze({
  LIVE: { label: "Live", tone: "live" },
  STILL: { label: "Live · not moving", tone: "live" },
  WAITING: { label: "Waiting for first location", tone: "pending" },
  NO_FIX: { label: "No GPS signal", tone: "warn" },
  STALE: { label: "Signal lost", tone: "warn" },
  ENDED: { label: "Journey ended", tone: "off" },
  OFF: { label: "GPS OFF", tone: "off" },
});

export const STOP_REASON_TEXT = Object.freeze({
  USER: "switched off",
  PERMISSION_DENIED: "location permission denied",
  JOURNEY_ENDED: "journey ended",
  SWITCHED_JOURNEY: "moved to another journey",
});

// "12 s ago" / "4 min ago" / "3 h ago" / a date.
export function timeAgo(ms, now = Date.now()) {
  if (!ms) return "never";
  const seconds = Math.max(0, Math.round((now - ms) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(ms).toLocaleString();
}

export function formatAccuracy(meters) {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return null;
  return meters >= 1000 ? `±${(meters / 1000).toFixed(1)} km` : `±${Math.round(meters)} m`;
}

// Path points ([{ id, session_id, user_id, lat, lng, accuracy, t }], in id
// order) -> one line per session, so a gap between two tracking sessions is not
// drawn as a straight jump. Returns Map(session_id -> { userId, coords: [[lng, lat]] }).
// Returns a NEW Map (the input is not mutated); each touched line is copied once.
export function appendToSessionLines(lines, points) {
  const next = new Map(lines);
  const copied = new Set();
  points.forEach((point) => {
    let line = next.get(point.session_id);
    if (!copied.has(point.session_id)) {
      line = { userId: point.user_id, coords: line ? line.coords.slice() : [] };
      next.set(point.session_id, line);
      copied.add(point.session_id);
    }
    line.coords.push([point.lng, point.lat]);
  });
  return next;
}

export function linesToGeoJson(lines, colorFor) {
  return {
    type: "FeatureCollection",
    features: [...lines.entries()]
      .filter(([, line]) => line.coords.length >= 2)
      .map(([sessionId, line]) => ({
        type: "Feature",
        properties: { session_id: sessionId, user_id: line.userId, color: colorFor(line.userId) },
        geometry: { type: "LineString", coordinates: line.coords },
      })),
  };
}

// A fixed colour per user (index in the order they first appear).
const USER_COLORS = ["#2563eb", "#db2777", "#16a34a", "#9333ea", "#ea580c", "#0891b2", "#b91c1c", "#4d7c0f"];
export function userColor(userIds, userId) {
  const index = userIds.indexOf(String(userId));
  return USER_COLORS[(index < 0 ? 0 : index) % USER_COLORS.length];
}
